import { forwardRef } from 'react';

import { Radio as BaseRadio } from '@base-ui/react/radio';
import { RadioGroup as BaseRadioGroup } from '@base-ui/react/radio-group';
import { cn } from 'components/utils';

import {
  radioControlRecipe,
  radioDotRecipe,
  radioGroupRecipe,
  radioLabelRecipe,
  radioRingRecipe,
  radioRootRecipe,
} from './Radio.recipe';

import type { RadioGroupProps, RadioProps } from './types';

/**
 * Design System 2.0 radio.
 *
 * Ported from Figma node 586:37614 (file 5aSkE0U4TeWbuIZDvccHjc), re-read on 2026-09-15 after the
 * component was reworked onto the semantic token layer. Checkbox shares the 18px shape and
 * choice-specific border role; larger fields continue to use `border/control`.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor through the token layer.
 *
 * Same shape as `Checkbox`: a real `<label>` wrapping Base UI's focusable `<span role="radio">`, so
 * the text is a click target and the accessible name needs no `id` plumbing. The ring is a child of
 * the control because the focus shadow is drawn on the 18px circle, and the label element is the one
 * that resolves hover and disabled for the whole row — see `Radio.recipe` for why.
 *
 * Figma's three variant properties map as runtime state rather than props, which is the point of the
 * mapping being deliberate: `State` (Default / Hover / Focus) is `:hover` and `:focus-visible`,
 * `isChecked` is Base UI's `data-checked`, and `isDisabled` is `disabled` — from this option OR from
 * the group around it. Figma's `Label` boolean, which hides the text layer, is `label` being
 * omitted; there is no separate flag, because a radio with no text still needs an `aria-label` and
 * the type already enforces that pairing.
 *
 * Normally rendered inside `RadioGroup` - see `RadioProps` for the one case where a lone radio makes
 * sense, and why it usually does not.
 */
export const Radio = forwardRef<HTMLElement, RadioProps>(
  ({ label, className, rootClassName, labelClassName, ...rest }, ref) => (
    <label className={cn(radioRootRecipe(), rootClassName)}>
      <BaseRadio.Root ref={ref} className={cn(radioControlRecipe(), className)} {...rest}>
        {/* `data-part` names the ring so a test can assert on it without depending on a styling
            class, as `Checkbox` and `TextInput` do for their own boxes. */}
        <span className={radioRingRecipe()} data-part="ring" />

        {/* Mounted only while selected, which Base UI handles. Unlike the checkbox there is no
            second glyph to swap to, so no CSS switch is needed here. */}
        <BaseRadio.Indicator className={radioDotRecipe()} data-part="dot" />
      </BaseRadio.Root>

      {label !== undefined && <span className={cn(radioLabelRecipe(), labelClassName)}>{label}</span>}
    </label>
  )
);

/**
 * A set of radios that behaves as one control.
 *
 * This is what a caller should almost always reach for. Base UI gives the group `role="radiogroup"`,
 * one tab stop for the whole set and arrow-key movement between the options - the composite
 * behaviour that hand-rolled radios routinely miss, and the reason this exists rather than leaving
 * callers to lay out bare `Radio`s themselves.
 *
 * It must be named. The type requires `aria-label` or `aria-labelledby`, and pointing the latter at
 * the question heading already on screen is usually the right answer - see `RadioGroupProps`.
 */
export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(({ className, ...rest }, ref) => (
  <BaseRadioGroup ref={ref} className={cn(radioGroupRecipe(), className)} {...rest} />
));
